import { Grid } from "@mui/material";
import { useCallback, useContext, useEffect } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useSetRecoilState } from "recoil";
import { noop } from "underscore";

import FilterActions from "../../../../../../../components/filter/filter-actions/FilterActions";
import FilterWrapper from "../../../../../../../components/filter/filter-wrapper/FilterWrapper";
import FormAutocompleteLocation from "../../_components/form-autocomplete-location/FormAutocompleteLocation";

import { pendingBookingsBySegmentAndCategoryFilterState } from "../../../../../recoil";
import { IFormValuesPendingBookingsBySegmentListDashboardFilter } from "../../../../../types";
import TenantForm from "../../../../../../../components/multitenant/TenantForm";
import DealerForm from "../../../../../../../components/multitenant/DealerForm";
import { GlobalContext } from "../../../../../../../../../util/state/GlobalContextProvider";
import useFormTenantsDealers from "../../_components/form-tenants-dealers-hook/useFormTenantsDealers";

/**
 * Filter component of pending bookings by location
 */
const FilterPendingBksBySegment = () => {

    const {globalData} = useContext(GlobalContext);
    const setPendingBookingsBySegmentAndCategoryFilter = useSetRecoilState(pendingBookingsBySegmentAndCategoryFilterState);

    const form = useForm<IFormValuesPendingBookingsBySegmentListDashboardFilter>({
        defaultValues: {
            pickupLocations: [],
            tenants: [],
            dealers: [],
        }
    });

    const tenantsWatch = useWatch({
        control: form.control,
        name: "tenants",
    });

    const dealersWatch = useWatch({
        control: form.control,
        name: "dealers",
    });

    const { setTenantsDealers } = useFormTenantsDealers();

    useEffect(() => {
        setTenantsDealers({ tenants: tenantsWatch, dealers: dealersWatch, })
    }, [tenantsWatch, dealersWatch]);

    const createPendingListBySegmentAndCategoryFilter = useCallback((data: IFormValuesPendingBookingsBySegmentListDashboardFilter): any => ({
        pickupLocations: data.pickupLocations.map(pl => pl.id),
        tenants: data.tenants,
        dealers: data.dealers,
    }), []);

    const handleSearch = useCallback((data: IFormValuesPendingBookingsBySegmentListDashboardFilter) => {
        setPendingBookingsBySegmentAndCategoryFilter(createPendingListBySegmentAndCategoryFilter(data));
    }, []);

    const handleReset = useCallback(() => {
        form.reset();
        setPendingBookingsBySegmentAndCategoryFilter(createPendingListBySegmentAndCategoryFilter(form.getValues()));
    }, []);

    useEffect(() => {
        if (globalData.user.legalEntities.length === 1) {
            form.setValue('tenants', globalData.user.legalEntities.map((elem: any) => elem.name));
        } else if (globalData.user.legalEntities.length > 1) {
            form.setValue('tenants', globalData.user.legalEntities.filter((elem: any) => elem.name !== 'all').map((elem: any) => elem.name));
            form.setValue('dealers', globalData.user.dealers.filter((elem: any) => elem.name !== 'all').map((elem: any) => elem.name));
        }

        setPendingBookingsBySegmentAndCategoryFilter(createPendingListBySegmentAndCategoryFilter(form.getValues()));
    }, []);

    return (
        <FilterWrapper>
            <FormProvider {...form}>
                <Grid container columnSpacing={5} alignItems="center">
                    <Grid item xs={12} md={6} lg={3}>
                        <TenantForm
                            name="tenants"
                            multiple={true}
                            onChange={() => form.setValue("dealers", [])}
                        />
                        </Grid>
                    <Grid item xs={12} md={6} lg={3}>
                        <DealerForm
                            name="dealers"
                            multiple={true}
                            tenants={tenantsWatch}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3} lg={3}>
                        <FormAutocompleteLocation />
                    </Grid>
                    <Grid item xs={12} sm={9} lg={9}>
                        <FilterActions
                            expanded={false}
                            onSearch={form.handleSubmit(handleSearch)}
                            onReset={handleReset}
                            onToggle={noop}
                            hideToogle={true}
                        />
                    </Grid>
                </Grid>
            </FormProvider>
        </FilterWrapper>
    );
}

export default FilterPendingBksBySegment;
